import { mergeSxProps, Pill, Text } from "@clipboard-health/ui-components";
import { Box, CardActionArea, Stack, type SxProps, type Theme } from "@mui/material";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useInView } from "react-intersection-observer";
import { useHistory, useLocation } from "react-router-dom";

import { APP_V2_APP_EVENTS, logEvent } from "../../../lib/analytics";
import { SHIFT_DISCOVERY_IN_APP_AD_MODAL_PATH } from "../../ShiftDiscovery/paths";
import { ShiftCardWrapper } from "../Card/Wrapper";
import { BookingCardContentWrapper } from "../CardContentWrapper";
import type { InferedInAppAdAttributesSchema } from "../Open/types";
import { ShiftCardPillsWrapper } from "../PillsWrapper";
import { TimeSlotIndicator } from "../TimeSlotIndicator";

interface InAppAdCardProps {
  inAppAd: InferedInAppAdAttributesSchema;
  sx?: SxProps<Theme>;
}

export function InAppAdCard(props: InAppAdCardProps): JSX.Element {
  const { inAppAd, sx } = props;
  const { title, shortDescription, pillTexts, incentivizeCopy, incentivizeAmount, appId, image } =
    inAppAd;
  const { url, width, height, parentPadding } = image;

  const history = useHistory();
  const location = useLocation();

  const [storedAdViewCounts, setStoredAdViewCounts] = useLocalStorage<Record<string, number>>(
    "storedAdViewCounts",
    {}
  );

  const { ref } = useInView({
    threshold: 0.5, // 50% of element must be visible
    triggerOnce: true, // Only trigger once per ad
    onChange: (inView) => {
      if (inView) {
        // Log analytics event
        logEvent(APP_V2_APP_EVENTS.AD_VIEWED, {
          app_id: appId,
          title,
          addDetails: inAppAd,
        });

        // Update view count in local storage
        const currentViewCount = storedAdViewCounts[appId] ?? 0;
        setStoredAdViewCounts({
          ...storedAdViewCounts,
          [appId]: currentViewCount + 1,
        });
      }
    },
  });

  return (
    <Box ref={ref}>
      <ShiftCardWrapper
        variant="quaternary"
        sx={mergeSxProps(
          {
            display: "flex",
            background: (theme) => theme.palette.shift?.inAppAds.background,
            border: (theme) => `1px solid ${theme.palette.shift?.priority.border}`,
          },
          sx
        )}
        data-testid={`in-app-ad-card-${appId}`}
      >
        <CardActionArea
          onClick={() => {
            logEvent(APP_V2_APP_EVENTS.AD_CLICKED, {
              app_id: appId,
              title,
              action: "view_details",
            });

            const currentPath = location.pathname;
            history.push(
              `${currentPath}/${SHIFT_DISCOVERY_IN_APP_AD_MODAL_PATH.replace(":adId", appId)}`
            );
          }}
        >
          <BookingCardContentWrapper>
            <Stack direction="row" alignItems="flex-start" spacing={5}>
              <TimeSlotIndicator
                sx={{
                  background: (theme) => theme.palette.shift?.inAppAds.timeSlotBackground,
                  padding: parentPadding ?? undefined,
                  "&::after": {
                    content: '""',
                    display: "block",
                    width,
                    height,
                    backgroundImage: `url(${url})`,
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  },
                }}
              />

              <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                <Text semibold variant="h6">
                  {title}
                </Text>

                <Text
                  variant="body2"
                  sx={(theme) => ({
                    color: theme.palette.text?.secondary,
                  })}
                >
                  {shortDescription}
                </Text>
              </Stack>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <ShiftCardPillsWrapper sx={{ flexWrap: "wrap" }}>
                {pillTexts.map((pill) => (
                  <Pill
                    key={`${pill.text}`}
                    size="small"
                    variant="outlined"
                    label={pill.text}
                    backgroundColor={(theme) => theme.palette.background.tertiary}
                    sx={{ width: "fit-content" }}
                  />
                ))}
              </ShiftCardPillsWrapper>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                sx={{ textAlign: "right" }}
                spacing={1}
              >
                <Text
                  variant="caption"
                  sx={(theme) => ({
                    color: theme.palette.text?.secondary,
                  })}
                >
                  {incentivizeCopy}
                </Text>
                <Text semibold variant="h6" sx={{ ml: 1 }}>
                  {incentivizeAmount}
                </Text>
              </Stack>
            </Stack>
          </BookingCardContentWrapper>
        </CardActionArea>
      </ShiftCardWrapper>
    </Box>
  );
}
